import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  MANAGEMENT_QUERY_KEY_GET_COMPANIES,
  MANAGEMENT_QUERY_KEY_GET_COMPANY,
  useManagementCreateCompany,
  useManagementDeleteCompany,
  useManagementGetCompanies,
  useManagementGetCompany,
  useManagementGetCompanyTypes,
  useManagementUpdateCompany
} from '@apps-common/apis/management/company'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements'
import { CheckboxInputSM, InputSM, SelectInputSM } from '@apps-common/components/elements/form'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { CompanyType } from '@apps-common/config/company'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '@apps-common/config/regex'
import { queryClient } from '@apps-common/lib'
import { ICompanyModel } from '@apps-common/types/management/company'

export const CompanyModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: { companyId: string }
}) => {
  const {
    isLoading: companiesLoading,
    isError: companiesError,
    data: companies
  } = useManagementGetCompanies({ name: '', type: CompanyType.MonitoringCentre })
  const { isLoading: ctIsLoading, isError: ctIsError, data: companyTypes } = useManagementGetCompanyTypes({})

  const {
    isLoading,
    isError,
    data: company
  } = useManagementGetCompany({
    companyId: componentProps?.companyId || '',
    config: {
      enabled: !!componentProps?.companyId
    }
  })

  const {
    reset,
    register,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm<ICompanyModel>({})

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_GET_COMPANY() })
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_GET_COMPANIES() })
    onDismiss(null, ModalDismiss.Confirm)
  }

  const { isPending: createIsLoading, mutate: createCompany } = useManagementCreateCompany(true, () => onChanged())
  const { isPending: updateIsLoading, mutate: updateCompany } = useManagementUpdateCompany(true, () => onChanged())
  const { isPending: deleteIsLoading, mutate: deleteCompany } = useManagementDeleteCompany(true, () => onChanged())

  useEffect(() => {
    if (company && companyTypes) {
      reset(company)
    }
  }, [company, companyTypes])

  const onSubmit = async (cyData: any) => {
    try {
      if (cyData.companyId) {
        updateCompany({ model: getValues() as any })
      } else {
        createCompany({ model: getValues() as any })
      }
    } catch (e: any) {
      // ignore, let mutation handle error
    }
  }

  const loading = companiesLoading || companiesError || ctIsLoading || ctIsError
  const updating = createIsLoading || updateIsLoading || deleteIsLoading

  return (
    <ModalContainer innerClasses='!min-h-[640px]'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle={componentProps?.companyId ? 'Updating' : 'Creating'} title='Company' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            {componentProps?.companyId && (
              <ModalButton
                component={ConfirmModal}
                disabled={loading || updating}
                classes=''
                componentProps={{
                  text: 'Delete',
                  description: 'Confirm you would like to delete this company. This cannot be undone.',
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => deleteCompany({ companyId: componentProps?.companyId })}
              >
                <ButtonRoundSm type='div' className='action-error'>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pt-[100px] !pb-[80px]'>
        {(!isLoading && isError) ||
          (!ctIsLoading && ctIsError && (
            <p className='mb-[10px] text-error text-sm'>Company failed to load. Please close window and try again.</p>
          ))}
        <form noValidate id='companyModalForm' onSubmit={handleSubmit(onSubmit)}>
          <div>
            <SelectInputSM
              type='text'
              label='Default monitoring centre'
              id='defaultMonitoringCentreId'
              showLabel
              className='capitalize'
              loading={loading}
              disabled={updating}
              registration={register('defaultMonitoringCentreId', {
                required: false
              })}
              options={[
                { label: '<None assigned>', value: '' },
                ...(companies?.map((c: any) => ({ label: c.companyName, value: c.companyId })) || [])
              ]}
              errors={errors.defaultMonitoringCentreId}
            />
            <SelectInputSM
              type='text'
              placeholder='Company type'
              label='Company type'
              id='companyTypeId'
              showLabel
              className='capitalize'
              loading={loading}
              disabled={updating}
              registration={register('companyTypeId', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={companyTypes?.map((ct) => ({ label: ct.companyTypeName, value: ct.companyTypeId })) || []}
              errors={errors.companyTypeId}
            />

            <InputSM
              type='text'
              placeholder='Name of company'
              label='Company name'
              id='companyName'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('companyName', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.companyName}
            />
            <InputSM
              type='text'
              placeholder='Display name of company'
              label='Display name'
              id='displayName'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('displayName', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.displayName}
            />
            <InputSM
              type='email'
              placeholder='Email for company'
              label='Email'
              id='email'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('email', {
                required: FORM_DEFAULT_REQUIRED,
                pattern: {
                  value: EMAIL_VALIDATION_REGEX,
                  message: 'Invalid email format'
                }
              })}
              errors={errors.email}
            />
            <InputSM
              type='text'
              placeholder='Phone for company'
              label='Phone'
              id='phone'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('phone', {
                required: FORM_DEFAULT_REQUIRED,
                pattern: {
                  value: PHONE_VALIDATION_REGEX,
                  message: 'Invalid phone format'
                }
              })}
              errors={errors.phone}
            />
            <InputSM
              type='number'
              placeholder='Discount applied for company'
              label='Discount'
              id='discount'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('discount', {
                required: FORM_DEFAULT_REQUIRED
              })}
              defaultValue={0}
              errors={errors.discount}
            />

            <CheckboxInputSM
              label='Company is charged directly'
              id='chargeDirect'
              loading={loading}
              disabled={updating}
              registration={register('chargeDirect')}
              errors={errors.chargeDirect}
            />
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRoundedSm
            form='companyModalForm'
            type='submit'
            loading={loading || updating}
            text={company ? 'Update' : 'Create'}
            className='action-success sm:grow-0'
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
